import type * as Y from 'yjs';

interface ScheduledInlineReveal {
  delayMs: number;
  seed: string;
  runId: string;
}

const pending = new WeakMap<Y.Text, ScheduledInlineReveal>();
const runs = new Map<string, Set<Y.Text>>();
const listeners = new Set<(runId: string) => void>();

export function takeInlineReveal(yText: Y.Text): ScheduledInlineReveal | null {
  const reveal = pending.get(yText);
  if (!reveal) return null;
  pending.delete(yText);
  const texts = runs.get(reveal.runId);
  texts?.delete(yText);
  if (!texts?.size) runs.delete(reveal.runId);
  return reveal;
}

/** One pending mount per text; replacing one also releases its old run entry. */
export function scheduleInlineReveal(
  yText: Y.Text,
  reveal: ScheduledInlineReveal
): void {
  takeInlineReveal(yText);
  pending.set(yText, reveal);
  let texts = runs.get(reveal.runId);
  if (!texts) runs.set(reveal.runId, (texts = new Set()));
  texts.add(yText);
}

export function finishInlineReveals(runId: string): void {
  for (const text of runs.get(runId) ?? []) pending.delete(text);
  runs.delete(runId);
  for (const listener of listeners) listener(runId);
}

export function onInlineRevealsFinished(
  listener: (runId: string) => void
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
